import { useMemo, useState } from 'react';
import {
  usePhasesTree,
  useCostTypes,
  useCreateItem,
  useUpdateItem,
  type ItemPayload,
  type ItemPhaseInput,
} from '../../api/masterTables';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { useToast } from '../../hooks/useToast';
import page from '../../pages/Page.module.css';
import { ITEMS_MASTER_HEADERS } from '../../lib/itemsMasterColumns';
import { isLaborCostTypeName } from '../../lib/itemMasterLabor';
import {
  itemAliasNames,
  parseItemAliases,
  type ItemAliasRow,
} from '../../lib/itemAliasesDisplay';

type ItemRow = Record<string, unknown>;

function ItemAliasesEditor({
  aliases,
  onChange,
}: {
  aliases: ItemAliasRow[];
  onChange: (next: ItemAliasRow[]) => void;
}) {
  const [draft, setDraft] = useState('');

  function addAlias() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    const exists = aliases.some((a) => a.alias_name.toLowerCase() === trimmed.toLowerCase());
    if (exists) return;
    onChange([...aliases, { alias_name: trimmed }]);
    setDraft('');
  }

  return (
    <div>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
        Aliases
      </span>
      <p className={page.muted} style={{ margin: '0 0 8px', fontSize: 13 }}>
        Alternate MTO / spreadsheet text that resolves to this item.
      </p>
      {aliases.length ? (
        <ul style={{ listStyle: 'none', margin: '0 0 10px', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {aliases.map((alias, index) => (
            <li
              key={alias.id != null ? `alias-${alias.id}` : `alias-new-${index}-${alias.alias_name}`}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <span style={{ flex: 1, fontSize: 14 }}>{alias.alias_name}</span>
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={() => onChange(aliases.filter((_, i) => i !== index))}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className={page.muted} style={{ margin: '0 0 10px', fontSize: 13 }}>
          No aliases yet.
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <Input
          label="Alias Text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addAlias();
            }
          }}
        />
        <Button type="button" variant="ghost" onClick={addAlias} disabled={!draft.trim()}>
          Add Alias
        </Button>
      </div>
    </div>
  );
}

export function ItemEditorForm({
  item,
  defaultName = '',
  onClose,
  onSuccess,
}: {
  item?: ItemRow;
  defaultName?: string;
  onClose: () => void;
  onSuccess?: () => void;
}) {
  const toast = useToast();
  const create = useCreateItem();
  const update = useUpdateItem();
  const { data: phasesTree } = usePhasesTree();
  const { data: costTypes } = useCostTypes();

  const initial = useMemo(() => {
    if (!item) {
      return {
        name: defaultName,
        itemCode: defaultName,
        unit: '',
        costTypeId: '',
        phaseId: '',
        subPhaseId: '',
        aliases: [] as ItemAliasRow[],
      };
    }
    const phases = Array.isArray(item.phases) ? (item.phases as Array<Record<string, unknown>>) : [];
    const primary = phases.find((p) => p.is_primary) || phases[0];
    return {
      name: String(item.name ?? ''),
      itemCode: String(item.item_code ?? ''),
      unit: String(item.unit ?? ''),
      costTypeId: item.cost_type_id != null ? String(item.cost_type_id) : '',
      phaseId:
        primary?.phase_id != null
          ? String(primary.phase_id)
          : item.phase_id != null
            ? String(item.phase_id)
            : '',
      subPhaseId:
        primary?.sub_phase_id != null
          ? String(primary.sub_phase_id)
          : item.sub_phase_id != null
            ? String(item.sub_phase_id)
            : '',
      aliases: parseItemAliases(item.aliases),
    };
  }, [item, defaultName]);

  const [name, setName] = useState(initial.name);
  const [itemCode, setItemCode] = useState(initial.itemCode);
  const [unit, setUnit] = useState(initial.unit);
  const [costTypeId, setCostTypeId] = useState(initial.costTypeId);
  const [phaseId, setPhaseId] = useState(initial.phaseId);
  const [subPhaseId, setSubPhaseId] = useState(initial.subPhaseId);
  const [aliases, setAliases] = useState<ItemAliasRow[]>(initial.aliases);

  const selectedCostTypeName = useMemo(() => {
    const ct = (costTypes ?? []).find((row) => String(row.id) === costTypeId);
    return ct ? String(ct.name ?? '') : '';
  }, [costTypes, costTypeId]);

  const isLabor = isLaborCostTypeName(selectedCostTypeName);

  function subPhaseOptionsFor(selectedPhaseId: string): Array<{ id: number; name: string }> {
    const ph = (phasesTree ?? []).find((p) => String(p.id) === selectedPhaseId);
    const subs = (ph?.sub_phases ?? []) as Array<Record<string, unknown>>;
    return subs.map((s) => ({ id: Number(s.id), name: String(s.name ?? '') }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      toast('Item ID required', true);
      return;
    }
    const phasesPayload: ItemPhaseInput[] =
      isLabor && phaseId
        ? [
            {
              phase_id: Number(phaseId),
              sub_phase_id: subPhaseId ? Number(subPhaseId) : null,
            },
          ]
        : [];
    const payload: ItemPayload = {
      name: name.trim(),
      item_code: itemCode.trim() || null,
      unit: unit.trim() || null,
      cost_type_id: costTypeId ? Number(costTypeId) : null,
      phases: phasesPayload,
      aliases: itemAliasNames(aliases),
    };
    try {
      if (item) {
        await update.mutateAsync({ id: Number(item.id), payload });
        toast('Item saved');
      } else {
        await create.mutateAsync(payload);
        toast('Item created');
      }
      onSuccess?.();
      onClose();
    } catch (ex) {
      toast(ex instanceof Error ? ex.message : 'Save failed', true);
    }
  }

  return (
    <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Input
        label={ITEMS_MASTER_HEADERS[0]}
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
      <Input
        label={ITEMS_MASTER_HEADERS[1]}
        value={itemCode}
        onChange={(e) => setItemCode(e.target.value)}
      />
      <Select
        label={ITEMS_MASTER_HEADERS[4]}
        value={costTypeId}
        onChange={(e) => {
          setCostTypeId(e.target.value);
          if (!isLaborCostTypeName(
            (costTypes ?? []).find((row) => String(row.id) === e.target.value)?.name
          )) {
            setPhaseId('');
            setSubPhaseId('');
          }
        }}
      >
        <option value="">None</option>
        {(costTypes ?? []).map((ct) => (
          <option key={String(ct.id)} value={String(ct.id)}>
            {String(ct.name)}
          </option>
        ))}
      </Select>
      {isLabor ? (
        <div className={page.row} style={{ alignItems: 'flex-end' }}>
          <Select
            label={ITEMS_MASTER_HEADERS[2]}
            value={phaseId}
            onChange={(e) => {
              setPhaseId(e.target.value);
              setSubPhaseId('');
            }}
          >
            <option value="">Select…</option>
            {(phasesTree ?? []).map((p) => (
              <option key={String(p.id)} value={String(p.id)}>
                {String(p.name)}
              </option>
            ))}
          </Select>
          <Select
            label={ITEMS_MASTER_HEADERS[3]}
            value={subPhaseId}
            onChange={(e) => setSubPhaseId(e.target.value)}
            disabled={!phaseId}
          >
            <option value="">None</option>
            {subPhaseOptionsFor(phaseId).map((sp) => (
              <option key={sp.id} value={String(sp.id)}>
                {sp.name}
              </option>
            ))}
          </Select>
        </div>
      ) : (
        <p className={page.muted} style={{ margin: 0, fontSize: 13 }}>
          Phase and sub phase for material and other items come from payable task group mappings on MTO
          lines.
        </p>
      )}
      <Input label={ITEMS_MASTER_HEADERS[5]} value={unit} onChange={(e) => setUnit(e.target.value)} />
      <ItemAliasesEditor aliases={aliases} onChange={setAliases} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="success" disabled={create.isPending || update.isPending}>
          Save
        </Button>
      </div>
    </form>
  );
}
